import { formatStatus } from '@ContractBuilder/components/Notifications';
import { useSchemaStore } from '@ContractBuilder/store';
import { FORM_UPDATED_NOUN, RESOURCE_NOUN, RESOURCE_SYNCED_LABEL } from '@features/activity/copy';
import type { AuditSurface } from '@root/@types/audit-log';
import clsx from 'clsx';
import { capitalize, get } from 'lodash-es';
import pluralize from 'pluralize';

import { bold, BOLD_NAME, NameList, Summary } from './primitives';
import type { SummarySlice } from './types';

// First source is the resolved schema ContractForm renders from, so datapoints are named as the form labels them.
// The schema-tree fallback still carries `if`-hidden fields but titles repeaters per repeat (" - Section N" / " N"), so the suffix comes back off.
const fieldTitle = (path: string): string => {
  const { resolvedSchema, findField } = useSchemaStore.getState();

  const formTitle: string | undefined = get(resolvedSchema, `properties.${path.split('.').join('.properties.')}.title`);
  if (formTitle) return formTitle;

  const treeTitle = findField(path, true, true)?.title;
  return treeTitle ? treeTitle.replace(/(?: - Section)? \d+$/, '') : (path.split('.').pop() ?? path);
};

const formNounSuffix = (surface: AuditSurface, count: number, verb: string) => {
  const noun = FORM_UPDATED_NOUN[surface];
  const datapoint = pluralize('datapoint', count);
  return noun === '' ? verb : noun ? `${noun} ${datapoint} ${verb}` : `${datapoint} ${verb}`;
};

export const resourceSummaries = {
  'resource.created': ({ entry }) => {
    const noun = capitalize(RESOURCE_NOUN[entry.surface]);
    if (!entry.createdFrom) return <div>{noun} created</div>;
    return (
      <Summary>
        {noun} created {entry.createdFrom.viaRenewal ? 'by renewing' : 'from'}&nbsp;
        {bold(entry.createdFrom.name)}
      </Summary>
    );
  },

  'renewal.created': ({ entry }) => (
    <Summary>Renewal&nbsp;{bold(entry.child.name)}&nbsp;created for this contract</Summary>
  ),

  'resource.synced': () => <div>{RESOURCE_SYNCED_LABEL}</div>,

  'name.changed': ({ entry }) => (
    <Summary>
      {capitalize(RESOURCE_NOUN[entry.surface])} renamed to&nbsp;{bold(entry.to)}
    </Summary>
  ),

  'cob.changed': ({ entry }) => <Summary>Class of business changed to&nbsp;{bold(entry.to)}</Summary>,

  'team.added': ({ entry, ctx }) => (
    <Summary>
      {bold(entry.teams.map(ctx.teamName).join(', '))}&nbsp;{pluralize('team', entry.teams.length)} added
    </Summary>
  ),

  'team.removed': ({ entry, ctx }) => (
    <Summary>
      {bold(entry.teams.map(ctx.teamName).join(', '))}&nbsp;{pluralize('team', entry.teams.length)} removed
    </Summary>
  ),

  'team.changed': ({ entry, ctx }) => <Summary>Team changed to&nbsp;{bold(ctx.teamName(entry.to))}</Summary>,

  'status.changed': ({ entry }) => (
    <div>
      {capitalize(RESOURCE_NOUN[entry.surface])} moved from{' '}
      <span className={clsx(BOLD_NAME, 'capitalize')}>{formatStatus(entry.from)}</span> to{' '}
      <span className={clsx(BOLD_NAME, 'capitalize')}>{formatStatus(entry.to)}</span> status
    </div>
  ),

  'base_template.changed': ({ entry }) => <div>Base template changed to&nbsp;{bold(entry.to)}</div>,

  'branding.changed': ({ entry }) => <Summary>Branding changed to&nbsp;{bold(entry.to)}</Summary>,

  'form.updated': ({ entry }) => (
    <div>
      <NameList names={entry.items.map(fieldTitle)} />
      &nbsp;{formNounSuffix(entry.surface, entry.items.length, 'edited')}
    </div>
  ),

  'datapoint.cleared': ({ entry }) => (
    <div>
      <NameList names={entry.names.map(fieldTitle)} />
      &nbsp;{formNounSuffix(entry.surface, entry.names.length, 'cleared')}
    </div>
  ),

  'heading.split': ({ entry }) => <Summary>{bold(fieldTitle(entry.name))}&nbsp;heading split into sections</Summary>,
} satisfies SummarySlice;
